import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { defaultAvatarImage, imageFormat, serverUrl, thumbSuffix, userImagesFolder } from "../../../config/env";
import AvatarImg from "../../../img/app/avatar_default_th.jpg";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import SearchFilterBar from "../../../components/commonUI/SearchFilterBar/SearchFilterBar";
import { FlaggedWarningIcon, DocumentIcon } from "../../../components/commonUI/Icons/SvgIcon";
import Popup from "../../../components/commonUI/Popup/Popup";
import DefaultImage from "../../../img/app/default-Image.webp";
import Pagination from "../../../components/commonUI/Pagination/Pagination";

const FlaggedImages = ({
  flaggedImages = [],
  onBlockImage = () => {},
  onAllowImage = () => {},
  loading = false,
  pageSize = 15,
  totalPages = 1,
  page = 1,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  filterCount = {},
}) => {
  const [statusFilter, setStatusFilter] = useState("allStatus");
  const [imageTypeFilter, setImageTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(page);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [inputValue, setInputValue] = useState(String(page));

  useEffect(() => {
    setCurrentPage(page);
    setInputValue(String(page));
  }, [page]);

  useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  const statusOptions = [
    { value: "allStatus", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "reviewed", label: "Reviewed" },
    { value: "approved", label: "Approved" },
  ];

  const imageTypeOptions = [
    { value: "all", label: "Any type" },
    { value: "avatar", label: "Avatar" },
    { value: "jobPhoto", label: "Job Photo" },
    { value: "document", label: "Document" },
  ];

  const tableColumns = [
    { key: "user", label: "User" },
    { key: "imageType", label: "Image type" },
    { key: "image", label: "Image" },
    { key: "reason", label: "Reason" },
    { key: "date", label: "Date" },
    { key: "actions", label: "Actions" },
  ];

  const filters = [
    {
      type: "select",
      key: "status",
      label: "Status",
      value: statusFilter,
      onChange: setStatusFilter,
      placeholder: "Select Status",
      options: statusOptions,
    },
    {
      type: "select",
      key: "imageType",
      label: "Image type",
      value: imageTypeFilter,
      onChange: setImageTypeFilter,
      placeholder: "Select Image Type",
      options: imageTypeOptions,
    },
    {
      type: "dateRange",
      key: "dateRange",
      label: "Date range",
      value: { start: startDate, end: endDate },
      onChange: (dates) => {
        setStartDate(dates.start);
        setEndDate(dates.end);
      },
      placeholder: "Any time",
    },
  ];

  const handleReset = () => {
    setStatusFilter("allStatus");
    setImageTypeFilter("all");
    setStartDate(null);
    setEndDate(null);
    onPageChange(1);
  };

  const getAvatarSrc = (user) => {
    if (user?.avatar && user.avatar !== "") {
      return `${serverUrl}${userImagesFolder}/${user?.shortId}/${user?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  };

  const renderImageRow = (flaggedImage, index) => {
    return (
    <tr key={index}>
      <td className="driver-avatar-cell">
        <div className="d-flex align-items-center g-12">
          <img
            src={getAvatarSrc(flaggedImage?.user)}
            className="avatar-pic"
            alt="User avatar"
            onError={(e) => {
              e.target.src = AvatarImg;
            }}
          />
          <div className="text-capitalize">
            {flaggedImage?.user?.firstname || "Anonymous"}
            <br />
            <span className="text-sm text-gray-600">
              {flaggedImage?.user?.shortId || "N/A"}
            </span>
          </div>
        </div>
      </td>

      <td className="driver-image-type-cell">
        <div>
          <div>
            <span
              className={`image-type-badge image-type-badge--${
                flaggedImage?.imageType === "avatar"
                  ? "avatar"
                  : flaggedImage?.imageType === "jobPhoto"
                  ? "job-photo"
                  : "document"
              }`}
            >
              {flaggedImage?.imageType === "avatar"
                ? "Avatar"
                : flaggedImage?.imageType === "jobPhoto"
                ? "Job Photo"
                : "Document"}
            </span>
          </div>
          <div className="d-flex align-items-center g-6 flagged-image-info-section">
            <DocumentIcon width="16" height="16" />
            <span className="text-sm font-weight-500">
              {flaggedImage?.user?.shortId || "N/A"}
            </span>
          </div>
        </div>
      </td>

      <td className="driver-image-cell">
      {flaggedImage?.image ? (
           <Popup
              trigger={
                <img
                  src={flaggedImage?.image}
                  alt="Flagged content"
                  className="flagged-image-thumbnail"
                  style={{ cursor: "pointer" }}
                  onClick={() => window.open(flaggedImage?.image, "_blank")}
                  onError={(e) => {
                    console.error("Image load failed:", flaggedImage?.image);
                    e.target.src = DefaultImage;
                  }}
                />
              }
              on="hover"
              flowing
              content={
                <img
                  src={flaggedImage?.image}
                  alt="Full flagged content"
                  className="flagged-image-preview__full"
                  onError={(e) => {
                    console.error("Image preview load failed:", flaggedImage?.image);
                    e.target.src = DefaultImage;
                  }}
                />
              }
            />
        ) : (
          <img src={DefaultImage} alt="No image available" className="flagged-image-thumbnail" />
        )}
      </td>

      <td className="driver-reason-cell">
        <div className="d-flex align-items-start g-8">
          <FlaggedWarningIcon className="flagged-icon" width="20" height="20" />
          <div>
            <div className="flagged-reason-text">Flagged for:</div>
            <div className="flagged-reason-text">
              {flaggedImage?.reason ||
                "Explicit image content"}
            </div>
          </div>
        </div>
      </td>

      <td className="driver-date-cell">
        {moment(flaggedImage?.flaggedAt).format("DD MMM YY")}
        <br />
        <span className="text-xs text-gray-600">
          {moment(flaggedImage?.flaggedAt).format("HH:mm")}
        </span>
      </td>

      <td className="driver-actions-cell">
        <div className="d-inline-flex align-items-center g-8">
          <button
            type="button"
            className="custom-btn btn-danger"
            onClick={() => onBlockImage(flaggedImage?._id)}
            disabled={loading}
          >
            Block
          </button>
          <button
            type="button"
            className="custom-btn btn-success"
            onClick={() => onAllowImage(flaggedImage?._id)}
            disabled={loading}
          >
            Allow
          </button>
        </div>
      </td>
    </tr>
    );
  };

  return (
    <div>
      <SearchFilterBar
        filters={filters}
        onReset={handleReset}
      />

      <DataTable
        data={flaggedImages}
        columns={tableColumns}
        renderRow={renderImageRow}
        onSort={() => {}}
        getSortClass={() => ""}
        emptyMessage="No Flagged Images Found"
        colSpan={6}
      />
      <Pagination
        pageSize={currentPageSize}
        totalPages={totalPages}
        page={currentPage}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setPageSize={(newSize) => {
          setCurrentPageSize(newSize);
          onPageSizeChange(newSize);
        }}
        setPage={(newPage) => {
          setCurrentPage(newPage);
          setInputValue(String(newPage));
          onPageChange(newPage);
        }}
      />
    </div>
  );
};

FlaggedImages.propTypes = {
  flaggedImages: PropTypes.array,
  onBlockImage: PropTypes.func,
  onAllowImage: PropTypes.func,
  loading: PropTypes.bool,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  filterCount: PropTypes.object,
};

export default FlaggedImages;
